import { AppBar as MuiAppBar, Box, Button, IconButton, ListItemIcon, MenuItem, Popover, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { ChevronLeft, EllipsisVertical, GraduationCap, LogOut, Sparkles } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRoute } from '../../router';
import { useEffect, useRef, useState } from 'react';
import { ViewToggle } from './view-toggle';
import { AppBarAgent } from './app-bar-agent';
import { CartButton } from './cart-button';
import { useScoring } from '../../../shared';
import { keycloak } from '@coverright/shared/keycloak';
import useLogo from './use-logo';

const shimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

interface AppBarProps {
  onScheduleClick?: () => void;
  onOpenCopilot?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AppBar({ onOpenCopilot }: AppBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const logo = useLogo();
  const [showBack, setShowBack] = useState(location.pathname.includes('plans') || location.pathname.includes('progress-tracker'));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setShowBack(location.pathname.includes('plans') || location.pathname.includes('progress-tracker'));
  }, [location.pathname]);

  const onBack = () => navigate(getRoute('situation'));

  return (
    <MuiAppBar position="fixed" elevation={0} sx={{ top: 0, zIndex: 50, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
      {/*
       * Layout strategy (mirrors the HTML prototype):
       *   Mobile (<md):  grid 2-col  — items placed via gridColumn/gridRow
       *   Desktop (md+): flex row    — two groups left/right
       *
       * Group 1 (display:contents on mobile) → its children go into parent grid
       * Group 2 (display:contents on mobile) → same
       */}
      <Box
        sx={{
          height: 57,
          display: { xs: 'grid', md: 'flex' },
          gridTemplateColumns: { xs: '1fr 1fr' },
          alignItems: { md: 'center' },
          justifyContent: { md: 'space-between' },
          py: { xs: 0, md: 1.5 },
          px: { xs: 0, md: 3 },
        }}
      >

        {/* ── Group 1: Logo + Edit Answers ─────────────────────────────────── */}
        <Box sx={{ display: { xs: 'contents', md: 'flex' }, alignItems: 'center', gap: 3 }}>

          {/* Logo — mobile: row1 col1 */}
          <Box
            sx={{
              gridColumn: 1, gridRow: 1,
              display: 'flex', alignItems: 'center',
              px: { xs: 3, md: 0 }, py: { xs: 1.5, md: 0 },
            }}
          >
            {logo}
          </Box>

          {/* Edit Answers — mobile: row2 col1 (with top border) */}
          {showBack && (
            <Box
              sx={{
                gridColumn: 1, gridRow: 2,
                display: 'flex', alignItems: 'center',
                borderTop: { xs: '1px solid  #e5e7eb', md: 'none' },
                bgcolor: '#fff',
                px: { xs: 3, md: 0 }, py: { xs: '10px', md: 0 },
              }}
            >
              <Button
                onClick={onBack}
                size="small"
                startIcon={<ChevronLeft size={12} />}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  height: 'auto',
                  minHeight: 'unset',
                  px: 0, py: 0,
                  '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
                }}
              >
                Edit Quiz
              </Button>
            </Box>
          )}
        </Box>

        {/* ── Group 2: Toggle + Agent ───────────────────────────────────────── */}
        <Box sx={{ display: { xs: 'contents', md: 'flex' }, alignItems: 'center', gap: 3 }}>

          <CartButton sx={{display: { xs: 'none', md: 'flex' }, flexShrink: 0}} />

          {/* View toggle — mobile: row2 col2 (with top border) */}
          {showBack && (
            <Box
              sx={{
                gridColumn: 2, gridRow: 2,
                display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', md: 'flex-start' },
                borderTop: { xs: '1px solid #e5e7eb', md: 'none' },
                bgcolor: '#fff',
                px: { xs: 3, md: 0 }, py: { xs: '10px', md: 0 },
              }}
            >
              <ViewToggle />
            </Box>
          )}

          {/* Cart button + Agent — mobile: row1 col2; desktop: border-l separator */}
          <Box
            sx={{
              gridColumn: 2, gridRow: 1,
              display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', md: 'flex-start' },
              px: { xs: 3, md: 0 }, py: { xs: 1.5, md: 0 },
              borderLeft: { md: '1px solid' },
              borderColor: { md: 'divider' },
              pl: { md: 3 },
              gap: 1.5,
            }}
          >

            <CartButton sx={{display: { xs: 'flex', md: 'none' }, flexShrink: 0}} />
            {/* Copilot button — mobile only */}
            {keycloak.authenticated && onOpenCopilot && (
              <>
                <Button
                  onClick={onOpenCopilot}
                  size="small"
                  startIcon={<Sparkles size={12} />}
                  sx={{
                    display: { xs: 'none', sm: 'flex', md: 'none' },
                    background: 'linear-gradient(135deg, #3b82f6, #9333ea)',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderRadius: 999,
                    height: 'auto',
                    minHeight: 'unset',
                    px: 1.5,
                    py: 0.75,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)',
                      animation: `${shimmer} 2s linear infinite`,
                      pointerEvents: 'none',
                    },
                    '&:hover': { opacity: 0.9, background: 'linear-gradient(135deg, #3b82f6, #9333ea)', boxShadow: 'none' },
                  }}
                >
                  Copilot
                </Button>
                <IconButton sx={{ display: { xs: 'flex', sm: 'none' }, flexShrink: 0,
                  background: 'linear-gradient(135deg, #3b82f6, #9333ea)',
                  height: 'auto',
                  borderRadius: 999,
                  color: '#fff', }} onClick={onOpenCopilot} >
                  <Sparkles size={14} />
                </IconButton>
              </>
            )}

            <AppBarAgent />

            {keycloak.authenticated && <IconButton size="small" ref={menuBtnRef} onClick={e => setMenuAnchor(e.currentTarget)}>
              <EllipsisVertical size={16} />
            </IconButton>}

            <Popover
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { mt: 0.5, minWidth: 160, borderRadius: 1, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } } }}
            >
              <MenuItem
                onClick={() => { setMenuAnchor(null); navigate(getRoute('learn')); }}
                sx={{ py: 1.25, px: 2, gap: 1 }}
              >
                  <GraduationCap size={16} color="#374151" />
                <Typography sx={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Learning Center</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => { setMenuAnchor(null); keycloak.logout(); }}
                sx={{ py: 1.25, px: 2, gap: 1 }}
              >
                  <LogOut size={16} color="#374151" />
                <Typography sx={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Log out</Typography>
              </MenuItem>
            </Popover>
          </Box>
        </Box>

      </Box>
    </MuiAppBar>
  );
}
